// ProfileTab — the identity read (name / username / email) and the one setting a
// person changes here: their TIME ZONE (ADR-082 §3 — the clock their screens,
// emails and messages use; set once at create, theirs to move). The field saves on
// change through useProfilePrefs (like theme + language — no save button). The
// read-only card the old single-page ProfilePage was; the full editing form is
// future work (APP-143).

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Picker } from '../../components/patterns/Picker.js';
import { useMe } from '../../hooks/useMe.js';
import { useProfilePrefs } from '../../hooks/useProfilePrefs.js';
import { timezoneSource } from '../../hooks/useTimezones.js';

export function ProfileTab() {
  const { t } = useTranslation('auth');
  const { data: me } = useMe();
  const prefs = useProfilePrefs();
  // The held zone stays a row even where the runtime lists it under another name.
  const zones = useMemo(() => timezoneSource([me?.user.timezone_iana]), [me?.user.timezone_iana]);
  if (!me) return null;

  return (
    <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg p-6 shadow-raised text-sm">
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <dt className="font-medium text-[var(--color-text-secondary)] mb-1">{t('profile.firstName')}</dt>
          <dd className="text-[var(--color-text-primary)]">{me.user.first_name}</dd>
        </div>
        <div>
          <dt className="font-medium text-[var(--color-text-secondary)] mb-1">{t('profile.lastName')}</dt>
          <dd className="text-[var(--color-text-primary)]">{me.user.last_name}</dd>
        </div>
        <div>
          <dt className="font-medium text-[var(--color-text-secondary)] mb-1">{t('profile.username')}</dt>
          <dd className="text-[var(--color-text-primary)] font-mono">{me.user.username}</dd>
        </div>
        <div>
          <dt className="font-medium text-[var(--color-text-secondary)] mb-1">{t('profile.email')}</dt>
          <dd className="text-[var(--color-text-primary)]">{me.user.email ?? t('profile.noEmail')}</dd>
        </div>
      </dl>
      <div className="mt-6 border-t border-[var(--color-divider)] pt-6 sm:max-w-md">
        <Picker
          source={zones}
          label={t('profile.timezone')}
          required
          value={me.user.timezone_iana}
          onChange={(v) => {
            if (v) prefs.setTimezone(v);
          }}
          help={t('profile.timezoneHelp')}
        />
      </div>
    </div>
  );
}

export default ProfileTab;
